import { useState } from 'react';
import { Link } from 'react-router-dom';
import { topicCards } from '@/mocks/home';

const levels = ['All', 'GCSE', 'A-Level'];

export default function TopicsSection() {
  const [activeLevel, setActiveLevel] = useState('All');

  const filtered = activeLevel === 'All'
    ? topicCards
    : topicCards.filter((t) => t.level === activeLevel);

  return (
    <section id="topics-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3 block">
            Curriculum Aligned
          </span>
          <h2
            className="text-3xl md:text-4xl font-extrabold text-slate-800 mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Browse by Topic
          </h2>
          <p className="text-slate-500 text-base max-w-xl mx-auto">
            Explore comprehensive revision materials for every topic in the UK Computer Science syllabus.
          </p>

          {/* Level Filter */}
          <div className="flex items-center justify-center mt-6">
            <div className="flex gap-1 bg-slate-100 rounded-full p-1">
              {levels.map((level) => (
                <button
                  key={level}
                  onClick={() => setActiveLevel(level)}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeLevel === level
                      ? 'bg-white text-emerald-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((topic) => (
            <Link
              key={topic.id}
              to={`/syllabus?topic=${topic.id}`}
              className="group bg-white border border-slate-100 rounded-xl p-6 hover:-translate-y-1 transition-all duration-200 cursor-pointer block"
              style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 flex items-center justify-center ${topic.color} rounded-xl`}>
                  <i className={`${topic.icon} ${topic.iconColor} text-xl`}></i>
                </div>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    topic.level === 'GCSE'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-violet-50 text-violet-700'
                  }`}
                >
                  {topic.level}
                </span>
              </div>

              <h3
                className="text-lg font-bold text-slate-800 mb-2 group-hover:text-emerald-700 transition-colors"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {topic.title}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-5">
                {topic.description}
              </p>

              {/* Progress */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-slate-400">
                    {topic.completed}/{topic.lessons} lessons
                  </span>
                  <span className="text-xs font-semibold text-emerald-700">
                    {Math.round((topic.completed / topic.lessons) * 100)}%
                  </span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-700 rounded-full transition-all"
                    style={{ width: `${(topic.completed / topic.lessons) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="flex items-center gap-1 mt-4 text-blue-600 text-sm font-medium">
                <span>Start Revising</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/syllabus"
            className="inline-flex items-center gap-2 border border-slate-200 hover:border-emerald-600 text-slate-600 hover:text-emerald-700 font-medium px-6 py-3 rounded-lg transition-all cursor-pointer text-sm"
          >
            View Full Syllabus
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
